import React from 'react';
import { Gem, TrendingUp, Globe, ExternalLink } from 'lucide-react';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { marketData } from '../../../data/terminalData';

interface InvestmentThesisPanelProps {
  isExpanded?: boolean;
}

export const InvestmentThesisPanel: React.FC<InvestmentThesisPanelProps> = ({ isExpanded = false }) => {
  const { ratings, fdi, wto } = marketData;

  // TOP 3 Investment Thesis Points
  const thesisPoints = [
    {
      number: 1,
      title: 'TRIPLE CREDIT UPGRADE',
      year: '2025',
      description: `S&P, Fitch → ${ratings.sp.rating} | Moody's outlook positive`,
      progress: 100,
      status: 'Historic',
      color: 'data-green',
    },
    {
      number: 2,
      title: 'RECORD FDI INFLOWS',
      year: `+${fdi.yoy}%`,
      description: `$${fdi.total2024}B in 2024, outpacing all regional peers`,
      progress: 95,
      status: 'Strong',
      color: 'data-green',
    },
    {
      number: 3,
      title: 'WTO ACCESSION',
      year: wto.target,
      description: `${wto.progress}% complete, unlocking global trade access`,
      progress: wto.progress,
      status: 'On Track',
      color: 'data-amber',
    },
  ];

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gem size={18} className="text-data-amber" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Investment Thesis</h2>
        </div>
        <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors">
          <span>Expand</span>
          <ExternalLink size={12} />
        </button>
      </div>

      {/* Thesis Points */}
      <div className="flex-1 p-5 space-y-4">
        {thesisPoints.map((point) => (
          <div key={point.number} className="relative">
            {/* Number Badge */}
            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full bg-${point.color}/20 border border-${point.color}/30 flex items-center justify-center shrink-0`}>
                <span className={`text-lg font-bold text-${point.color}`}>{point.number}</span>
              </div>

              <div className="flex-1 min-w-0">
                {/* Title Row */}
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-bold text-white tracking-wide">{point.title}</h3>
                  <span className={`text-sm font-mono font-bold text-${point.color}`}>{point.year}</span>
                </div>

                {/* Description */}
                <p className="text-xs text-gray-400 mb-2">{point.description}</p>

                {/* Progress Bar */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-terminal-border rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-${point.color} transition-all duration-500`}
                      style={{ width: `${point.progress}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold text-${point.color}`}>{point.status}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );
};

export default InvestmentThesisPanel;
